import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { User } from '../models/user.model';
import { Subscription } from '../models/subscription.model';
import { MerchantType } from '../models/onboarding.model';
import { environment } from '../../environments/environment';
import { AnalyticsService } from './analytics.service';
import { LogService } from './log.service';
import { ThemeService } from '../shared/services/theme.service';

// DÉTAILS: L'interface pour la réponse de l'API de connexion/inscription.
export interface AuthResponse {
  token: string;
  user: User;
  subscription: Subscription;
}

/** Double authentification active : aucun jeton de session tant que le code n'est pas vérifié. */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeToken: string;
  message?: string;
}

export type LoginResult = AuthResponse | TwoFactorChallenge;

export const isTwoFactorChallenge = (
  result: LoginResult,
): result is TwoFactorChallenge =>
  (result as TwoFactorChallenge).twoFactorRequired === true;

interface RegisterPayload {
  email: string;
  password: string;
  businessName: string;
  acceptedCGU?: boolean;
  selectedPlan?: string;
}

/**
 * DÉTAILS: Ce service gère TOUT ce qui est lié à l'authentification.
 * - Stockage du token
 * - État de connexion (connecté/déconnecté)
 * - Communication avec les endpoints d'authentification du backend
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private analytics = inject(AnalyticsService);
  private log = inject(LogService);
  private themeService = inject(ThemeService);

  //  private apiUrl = 'http://analyticai.com:3000/api/auth';
  private apiUrl = '/api/auth';
  private tokenKey = 'token';
  private subscriptionKey = 'subscription'; // Clé pour stocker l'abonnement

  // Signal state
  private _isLoggedIn = signal<boolean>(this.hasToken());
  readonly isLoggedIn = this._isLoggedIn.asReadonly();

  // Pour le flux de vérification d'e-mail
  private pendingVerificationEmail: string | null = null;
  // DÉTAILS: Stocke temporairement le plan choisi lors de l'inscription payante.
  private pendingPlanId: string | null = null;

  private _currentUser = signal<Partial<User> | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  private _isOnboarded = signal<boolean>(false);
  readonly isOnboarded = this._isOnboarded.asReadonly();

  private _subscription = signal<Subscription | null>(
    this.getSubscriptionFromStorage(),
  );
  readonly subscription = this._subscription.asReadonly();

  /**
   * Vérifie si un token est présent dans le sessionStorage.
   * sessionStorage est utilisé pour isoler les sessions entre fenêtres/onglets.
   * @returns `true` si un token existe, sinon `false`.
   */
  private hasToken(): boolean {
    // 🎭 Mode mock : toujours connecté, pas de vrai JWT nécessaire
    if (environment.mockApi) {
      return true;
    }

    const token = sessionStorage.getItem(this.tokenKey);
    if (token) {
      try {
        const decoded: { exp: number } = jwtDecode(token);
        // Vérifie si le token est expiré
        return Date.now() < decoded.exp * 1000;
      } catch (e) {
        this.clearSessionStorage();
        return false;
      }
    }
    return false;
  }

  /**
   * Expose l'état de connexion actuel de manière synchrone.
   */
  getIsLoggedIn(): boolean {
    return this._isLoggedIn();
  }

  /**
   * Enregistre un nouvel utilisateur.
   */
  register(payload: RegisterPayload): Observable<any> {
    this.pendingVerificationEmail = payload.email;
    return this.http
      .post(`${this.apiUrl}/register`, payload)
      .pipe(tap(() => this.analytics.trackRegister()));
  }

  /**
   * Connecte un utilisateur.
   * En cas de succès, stocke le token et met à jour l'état de connexion.
   * Si la double authentification est active, ne stocke rien : la connexion se termine avec loginTwoFactor.
   */
  login(email: string, password: string): Observable<LoginResult> {
    return this.http
      .post<LoginResult>(`${this.apiUrl}/login`, { email, password })
      .pipe(
        tap((response) => {
          if (!isTwoFactorChallenge(response)) this.completeLogin(response);
        }),
      );
  }

  /** Seconde étape de connexion : code de l'application d'authentification ou code de secours. */
  loginTwoFactor(
    challengeToken: string,
    code: string,
  ): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/login/2fa`, { challengeToken, code })
      .pipe(tap((response) => this.completeLogin(response)));
  }

  private completeLogin(response: AuthResponse): void {
    // 1️⃣ Stocker le token
    this.setToken(response.token);

    // 2️⃣ Stocker l'utilisateur complet dans le sessionStorage (isolé par fenêtre)
    sessionStorage.setItem('user', JSON.stringify(response.user));
    this._currentUser.set(response.user);

    // 3️⃣ Stocker l'abonnement
    this.setSubscription(response.subscription);

    // 4️⃣ Mettre à jour l'état de connexion
    this._isLoggedIn.set(true);

    // 5️⃣ Identifier le marchand dans Matomo
    const userId = response.user?._id || response.user?.id;
    if (userId) {
      this.analytics.setUserId(userId);
      this.themeService.onLogin(userId);
    }
    this.analytics.trackLogin(response.user?.merchantType as string);
  }

  /**
   * Déconnecte l'utilisateur, supprime le token et redirige.
   */
  logout(queryParams?: Record<string, string>): void {
    // Tracker avant de réinitialiser
    this.analytics.trackLogout();
    this.analytics.resetUserId();
    this.themeService.onLogout();

    sessionStorage.removeItem(this.tokenKey);
    this.clearSessionStorage();

    this._isLoggedIn.set(false);
    this.router.navigate(['/login'], { queryParams });
  }

  /**
   * Valide le token de vérification d'e-mail auprès du backend.
   */
  verifyEmail(token: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/verify-email`, { params: { token } });
  }

  resendVerification(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/resend-verification`, { email });
  }

  /**
   * 🔐 Demande une réinitialisation du mot de passe
   * @param email - Email de l'utilisateur
   * @returns Observable avec la réponse du backend
   */
  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/forgot-password`, { email });
  }

  /**
   * 🔐 Valide un token de réinitialisation
   * @param token - Token de réinitialisation
   * @param email - Email de l'utilisateur
   * @returns Observable avec validation status
   */
  validateResetToken(token: string, email: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/validate-reset-token`, {
      params: { token, email },
    });
  }

  /**
   * 🔐 Réinitialise le mot de passe
   * @param token - Token de réinitialisation
   * @param email - Email de l'utilisateur
   * @param password - Nouveau mot de passe
   * @returns Observable avec la réponse du backend
   */
  resetPassword(
    token: string,
    email: string,
    password: string,
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, {
      token,
      email,
      password,
    });
  }

  getPendingVerificationEmail(): string | null {
    return this.pendingVerificationEmail;
  }

  /**
   * DÉTAILS: Permet de récupérer le plan en attente après la connexion.
   * Il est ensuite effacé pour ne pas être réutilisé.
   */
  getPendingPlan(): string | null {
    const plan = sessionStorage.getItem('selected_plan');

    this.log.debug('getPendingPlan retrieved');

    if (plan) {
      //sessionStorage.removeItem('selected_plan');
    }

    return plan;
  }

  /**
   * Stocke le token JWT dans le sessionStorage (isolé par fenêtre/onglet).
   * Met aussi à jour le user dans sessionStorage et le currentUserSubject.
   */
  setToken(token: string): void {
    if (!token || typeof token !== 'string') {
      this.log.error('Token invalide');
      return;
    }

    sessionStorage.setItem('token', token);

    // Décoder le token et mettre à jour le user stocké
    try {
      const decoded: any = jwtDecode(token);
      const userFromToken: Partial<User> = {
        id: decoded.id,
        email: decoded.email,
        onboardingStep: decoded.onboardingStep,
        onboardingCompleted: decoded.onboardingCompleted,
        profileCompleteness: decoded.profileCompleteness,
        merchantType: decoded.merchantType,
      };

      // Mettre à jour le sessionStorage user
      sessionStorage.setItem('user', JSON.stringify(userFromToken));

      // Mettre à jour le signal
      this._currentUser.set(userFromToken as User);

      // Mettre à jour le status onboarded
      if (decoded.onboardingCompleted) {
        this.setOnboarded(true);
      }

      this.log.debug('User updated from token', { userId: userFromToken.id });
    } catch (err) {
      this.log.error('Erreur décodage token dans setToken', {
        error: (err as Error).message,
      });
    }
  }

  /**
   * Récupère le token JWT depuis le sessionStorage.
   */
  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  /**
   * DÉTAILS: Méthodes pour gérer le stockage de l'abonnement (sessionStorage pour isolation).
   */
  setSubscription(subscription: Subscription): void {
    sessionStorage.setItem(this.subscriptionKey, JSON.stringify(subscription));
    this._subscription.set(subscription);
  }

  getSubscription(): Subscription | null {
    return this._subscription();
  }

  private getSubscriptionFromStorage(): Subscription | null {
    const sub = sessionStorage.getItem(this.subscriptionKey);
    return sub ? JSON.parse(sub) : null;
  }

  /**
   * Adapter setCurrentUser pour initialiser l'onboarding status
   */
  setCurrentUser(user: Partial<User>): void {
    // Code existant...
    this._currentUser.set(user as User);

    // NOUVEAU : marquer comme onboardé si présent en BD
    if (user && user.onboardingCompleted) {
      this.setOnboarded(true);
    }
  }

  /**
   * Décode le token pour obtenir les informations de l'utilisateur sans appeler le backend.
   */
  getCurrentUser(): Partial<User> | null {
    // 🎭 Mode mock : retourner un utilisateur fictif
    if (environment.mockApi) {
      return {
        email: 'demo@predivent.com',
        businessName: 'Les Crêpes de Marie',
        merchantType: MerchantType.ITINERANT,
        onboardingCompleted: true,
      } as Partial<User>;
    }

    const userJson = sessionStorage.getItem('user');
    if (userJson) {
      try {
        return JSON.parse(userJson);
      } catch (err) {
        this.log.error('Erreur parsing user from sessionStorage');
        this.logout();
        return null;
      }
    }

    // Fallback sur JWT si user non présent (optionnel)
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded: any = jwtDecode(token);
      return {
        id: decoded.id,
        email: decoded.email,
        onboardingStep: decoded.onboardingStep,
        onboardingCompleted: decoded.onboardingCompleted,
        // autres champs si présents dans le token
      };
    } catch (err) {
      this.log.error('Erreur de décodage JWT');
      this.logout();
      return null;
    }
  }

  refreshCurrentUser(): void {
    const user = this.getCurrentUser();
    this._currentUser.set(user);
  }

  setSelectedPlan(plan: string) {
    sessionStorage.setItem('selected_plan', plan);
  }

  getSelectedPlan(): string | null {
    return sessionStorage.getItem('selected_plan');
  }

  clearSelectedPlan() {
    sessionStorage.removeItem('selected_plan');
  }

  private clearSessionStorage(): void {
    sessionStorage.removeItem(this.tokenKey);
    sessionStorage.removeItem(this.subscriptionKey);
    sessionStorage.removeItem('user');
    this._subscription.set(null);
  }

  /**
   * Marquer l'utilisateur comme onboardé
   */
  setOnboarded(isOnboarded: boolean): void {
    this._isOnboarded.set(isOnboarded);
    // Optionnel : sauvegarder dans sessionStorage (isolé par fenêtre)
    if (isOnboarded) {
      sessionStorage.setItem('isOnboarded', 'true');
    } else {
      sessionStorage.removeItem('isOnboarded');
    }
  }

  /**
   * Vérifier si l'utilisateur est onboardé
   */
  getIsOnboarded(): boolean {
    return this._isOnboarded();
  }

  /**
   * Initialiser depuis sessionStorage au démarrage
   */
  private initializeFromSessionStorage(): void {
    const onboarded = sessionStorage.getItem('isOnboarded') === 'true';
    this._isOnboarded.set(onboarded);
  }

  /**
   * Récupère le profil utilisateur à jour depuis le backend
   */
  getUserProfile(): Observable<{ success: boolean; data: User }> {
    return this.http
      .get<{ success: boolean; data: User }>(`/api/auth/profile`)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            // Mettre à jour le user dans sessionStorage
            sessionStorage.setItem('user', JSON.stringify(response.data));
            this._currentUser.set(response.data);
          }
        }),
      );
  }

  /**
   * Récupère les infos complètes de l'utilisateur depuis le backend
   */
  getMe(): Observable<{ user: User; subscription: any }> {
    return this.http
      .get<{ user: User; subscription: any }>(`/api/auth/me`)
      .pipe(
        tap((response) => {
          if (response.user) {
            // Mettre à jour le user dans sessionStorage
            sessionStorage.setItem('user', JSON.stringify(response.user));
            this._currentUser.set(response.user);
          }
          if (response.subscription) {
            this.setSubscription(response.subscription);
          }
        }),
      );
  }

  /**
   * Met à jour le profil utilisateur
   */
  updateProfile(
    profileData: any,
  ): Observable<{ success: boolean; data: User; message: string }> {
    return this.http
      .put<{
        success: boolean;
        data: User;
        message: string;
      }>(`/api/auth/profile`, profileData)
      .pipe(
        tap((response) => {
          if (response.success && response.data) {
            // Mettre à jour le user dans sessionStorage
            sessionStorage.setItem('user', JSON.stringify(response.data));
            this._currentUser.set(response.data);
          }
        }),
      );
  }
}
